import { useState } from "react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import {
  ChevronRight,
  Mic,
  Star,
  UserRound,
  Zap,
  MessageCircle,
  Sparkles,
  Headphones,
  Headset,
  Settings,
  Sliders,
  Network,
  Award,
  CreditCard,
  ShoppingBag,
  Gamepad2,
  Radio,
  Megaphone,
  Building2,
  GraduationCap,
  Plane,
  Shield,
  Bell,
  Clock,
  Wallet,
  MessageSquare,
  Plug,
  TrendingUp,
  AudioWaveform,
  Lock,
  Phone,
  ShieldCheck,
  KeyRound,
  Building,
  UserCheck,
  Key,
  Receipt,
  Mail,
  ShieldAlert,
  type LucideIcon,
} from "lucide-react";
import SiteHeader from "@/components/site/SiteHeader";
import SiteFooter from "@/components/site/SiteFooter";
import { Link } from "react-router-dom";
import { CtaTopScribble, CtaUnderlineScribble, VectorWordmark, YootelcoLogo } from "@/components/site/VectorMarks";

import ReachingYooTicker from "@/components/site/ReachingYooTicker";
import UpcomingEvents from "@/components/UpcomingEvents";
import Seo from "@/components/Seo";

const exploreGroups = [
  {
    title: "Products",
    links: [
      { title: "A2P SMS", to: "/solutions/a2p-sms", Icon: MessageSquare },
      { title: "OTP & Authentication", to: "/solutions/otp-authentication", Icon: Lock },
      { title: "Voice Termination", to: "/solutions/voice-termination", Icon: Phone },
    ],
  },
  {
    title: "Use Cases",
    links: [
      { title: "OTP Verification", to: "/use-cases/otp-verification", Icon: ShieldCheck },
      { title: "Transactional Alerts", to: "/use-cases/transactional-alerts", Icon: Receipt },
      { title: "Platform Notifications", to: "/use-cases/platform-notifications", Icon: Bell },
      { title: "Payment & Fintech", to: "/use-cases/payment-fintech", Icon: CreditCard },
    ],
  },
  {
    title: "Industries",
    links: [
      { title: "Fintech & Payments", to: "/industries/fintech-payments", Icon: CreditCard },
      { title: "iGaming", to: "/industries/igaming", Icon: Gamepad2 },
      { title: "Telecom & CPaaS", to: "/industries/telecom-cpaas", Icon: Radio },
      { title: "Education Marketing", to: "/industries/education-marketing", Icon: GraduationCap },
    ],
  },
];

const navItems = [
  { label: "Solutions", hasChevron: true },
  { label: "Use Cases", hasChevron: true },
  { label: "Industries", hasChevron: false },
  { label: "Coverage & Routes", hasChevron: false },
  { label: "About", hasChevron: false },
  { label: "Blog", hasChevron: false },
];

// solutionsMenu now lives in SiteHeader

const whyItems = [
  { title: "Telecom-Native Expertise", description: "18+ years of operational experience in voice and messaging", Icon: Headphones, tone: "amber" },
  { title: "Client-Centric Support", description: "Real-time support, instant communication channels, fast interactive resolution", Icon: Headset, tone: "sky" },
  { title: "Carrier-Grade Reliability", description: "Built on telecom DNA, delivering carrier-level reliability and uptime for mission-critical messaging and voice traffic.", Icon: Settings, tone: "violet" },
  { title: "Service Flexibility", description: "Flexible solutions, faster decisions, and adaptable service models for different business needs", Icon: Sliders, tone: "lime" },
  { title: "Broad Connectivity and Vendor Portfolio", description: "Access to a wide range of trusted connectivity providers and technology vendors", Icon: Network, tone: "pink" },
  { title: "Experience", description: "Own a fintech company solving delivery failure challenges", Icon: Award, tone: "brand" },
] as const;

const whyTone: Record<string, string> = {
  amber: "bg-amber-soft text-amber",
  sky: "bg-sky-soft text-sky",
  violet: "bg-violet-soft text-violet",
  lime: "bg-lime-soft text-lime",
  pink: "bg-[hsl(330_100%_96%)] text-[hsl(330_82%_60%)]",
  brand: "bg-brand-soft text-brand",
};

const useCases = [
  {
    title: "OTP Verification",
    description: "Secure login and transaction verification.",
    headerClass: "usecase-header-blue",
    accentTitle: false,
    decor: [
      { Icon: Shield, className: "h-[26px] w-[26px] left-[48%] top-[22px]" },
      { Icon: Star, className: "h-[16px] w-[16px] left-[29%] bottom-[20px]" },
      { Icon: Star, className: "h-[16px] w-[16px] left-[40%] bottom-[20px]" },
      { Icon: Star, className: "h-[16px] w-[16px] left-[51%] bottom-[20px]" },
      { Icon: Star, className: "h-[16px] w-[16px] left-[62%] bottom-[20px]" },
    ] as { Icon: LucideIcon; className: string }[],
    detail:
      "Secure user authentication begins with generating a unique one-time password whenever a login or transaction is initiated. Our system verifies the OTP in real time to ensure accuracy and security. Each code expires within a short time window to prevent unauthorized use. This process provides a fast, reliable, and secure layer of protection for your platform and users.",
  },
  {
    title: "Transactional Alerts",
    description: "Order confirmations and account notifications.",
    headerClass: "usecase-header-teal",
    accentTitle: false,
    decor: [
      { Icon: MessageSquare, className: "h-[28px] w-[28px] left-[28%] top-[32px]" },
      { Icon: Bell, className: "h-[26px] w-[26px] left-[53%] top-[54px]" },
    ] as { Icon: LucideIcon; className: string }[],
    detail:
      "Keep customers informed in real time with reliable transactional alerts for order confirmations, shipping updates, account activity, and service notifications. Messages are delivered through high-priority routes to ensure every alert reaches the user instantly, building trust and reducing support inquiries.",
  },
  {
    title: "Marketing",
    description: "User engagement and re-activation messaging.",
    headerClass: "usecase-header-violet",
    accentTitle: false,
    decor: [
      { Icon: MessageSquare, className: "h-[25px] w-[25px] left-[58%] top-[34px] rotate-[-20deg]" },
      { Icon: Clock, className: "h-[28px] w-[28px] left-[26%] top-[52px]" },
    ] as { Icon: LucideIcon; className: string }[],
    detail:
      "Drive engagement with targeted promotional campaigns, personalized offers, and re-activation messaging at scale. Reach customers through high-throughput messaging routes with delivery insights that help you optimize timing, content, and audience segmentation for measurable campaign performance.",
  },
  {
    title: "Payments & Fintech",
    description: "Secure payments and fraud prevention.",
    headerClass: "usecase-header-lime",
    accentTitle: false,
    detail:
      "Enable secure and seamless digital payments through a reliable fintech infrastructure. Our platform supports fast transaction processing for a wide range of payment use cases. Built-in security and compliance help protect every transaction and user interaction. Easily integrate payment capabilities into your applications and services. Real-time processing ensures a smooth and efficient payment experience. This makes the solution ideal for fintech platforms that require both speed and trust.",
    decor: [
      { Icon: Wallet, className: "h-[24px] w-[24px] left-[28%] top-[30px]" },
      { Icon: Shield, className: "h-[26px] w-[26px] left-[58%] top-[54px]" },
    ] as { Icon: LucideIcon; className: string }[],
  },
];

const solutionCards = [
  {
    title: "Messaging solutions",
    description:
      "Scalable A2P SMS solutions with global reach, reliable delivery, and support for enterprise messaging use cases.",
  },
  {
    title: "Voice Solutions & Authentication",
    description: "High-quality voice traffic support with optimized routing and competitive rates",
  },
];

const industries = [
  { title: "Fintech & Payments", Icon: CreditCard, color: "text-amber", lines: ["Secure OTP delivery", "Transaction alerts"] },
  { title: "Retail & Ecomerce", Icon: ShoppingBag, color: "text-sky", lines: ["Promotional SMS campaigns", "Order & delivery updates"] },
  { title: "iGaming", Icon: Gamepad2, color: "text-lime", lines: ["Instant verification messages", "High-volume SMS routing"] },
  { title: "Telecom & CPaaS", Icon: Radio, color: "text-brand", lines: ["Carrier-grade messaging", "Global voice connectivity"] },
  { title: "Digital Marketing Agencies", Icon: Megaphone, color: "text-violet", lines: ["Bulk campaign delivery", "Customer engagement tools"] },
  { title: "Real Estate", Icon: Building2, color: "text-sky", lines: ["Lead notification systems", "Client communication flows"] },
  { title: "Education Marketing", Icon: GraduationCap, color: "text-amber", lines: ["Student engagement messaging", "Application status alerts"] },
  { title: "Travel & Transportation", Icon: Plane, color: "text-brand", lines: ["Booking confirmation SMS", "Real-time travel updates"] },
] as const;

const faqs = [
  {
    question: "What is A2P SMS and how does it differ from P2P?",
    answer:
      "A2P SMS is application-to-person messaging used for alerts, OTPs, and notifications. Unlike person-to-person traffic, it is designed for business-grade throughput, compliance, and delivery visibility.",
  },
  {
    question: "Do you support both SMS and voice routing?",
    answer:
      "Yes. The platform supports reliable A2P messaging and voice solutions with optimized routes, delivery controls, and enterprise support.",
  },
  {
    question: "How quickly can we integrate?",
    answer:
      "Most teams can launch quickly using REST APIs or SMPP, with onboarding guidance from solution engineers for production readiness.",
  },
  {
    question: "Can you support regional coverage requirements?",
    answer:
      "Coverage and routing are tailored around target markets, sender needs, and local traffic expectations to help maintain quality and compliance.",
  },
  {
    question: "Do you provide reporting and delivery monitoring?",
    answer:
      "Yes. Teams get ongoing visibility into delivery performance, route quality, and message outcomes so they can optimize campaigns and critical flows.",
  },
];

const partners = [
  { name: "BTS", src: "/logos/bts.svg" },
  { name: "PCCW", src: "/logos/pccw.webp" },
  { name: "Hot Mobile", src: "/logos/hot-mobile.webp" },
  { name: "Telecel", src: "/logos/telecel.webp" },
  { name: "Telefónica", src: "/logos/telefonica.webp" },
  { name: "Airtel", src: "/logos/airtel.webp" },
  { name: "Infobip", src: "/logos/infobip.svg" },
  { name: "JT", src: "/logos/jt.webp" },
  { name: "China Mobile International", src: "/logos/china-mobile.webp" },
];
const icons = ["◐", "◎", "◌", "◈", "◇", "▣", "✧", "⬢"];

const Index = () => {
  const [openCase, setOpenCase] = useState<string | null>(null);
  return (
    <main className="min-h-screen bg-white">
      <Seo
        title="Yootelco | A2P SMS, OTP Verification & Voice Termination"
        description="Telecom-grade A2P SMS, OTP verification, and voice termination for MNOs, fintechs, banks and enterprises. Direct routes, real-time delivery, global coverage."
        path="/"
        faqs={faqs}
      />
      <SiteHeader />

      <section id="top" className="pb-10 pt-[120px] md:pb-12 md:pt-[140px]" style={{ backgroundImage: "var(--gradient-hero)" }}>
        <div className="container mx-auto max-w-[1080px] text-center">
          <div className="mx-auto pb-0 pt-8 md:pt-6">
            <h1 className="mx-auto max-w-[860px] font-display text-[48px] font-bold leading-[1.04] tracking-tight text-foreground md:text-[64px] md:leading-[1.06]">
              Messaging and Voice Solutions for Global Businesses
            </h1>
            <p className="mx-auto mt-[16px] max-w-[620px] text-[15px] font-medium leading-[1.5] text-muted-foreground md:text-[16px]">
              18+ years of operational experience in voice and messaging
            </p>
            <Link
              to="/contact"
              className="mt-[28px] inline-flex rounded-[6px] bg-primary px-[31px] py-[13px] text-[15px] font-bold text-primary-foreground shadow-soft transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lift"
            >
              Contact Sales
            </Link>
          </div>
        </div>
      </section>

      <section id="why-yootelco" className="section-shell">
        <div className="text-center">
          <h2 className="why-title">Why Yootelco</h2>
        </div>
        <div className="mx-auto mt-[34px] grid max-w-[1280px] gap-5 md:grid-cols-2 xl:grid-cols-3">
          {whyItems.map((item) => (
            <article key={item.title} className="why-card p-[30px] text-center md:min-h-[232px]">
              <div className={`mx-auto flex h-[52px] w-[52px] items-center justify-center rounded-[14px] ${whyTone[item.tone]}`}>
                <item.Icon className="h-[26px] w-[26px]" strokeWidth={2} aria-hidden="true" />
              </div>
              <h3 className="mx-auto mt-[18px] max-w-[280px] text-[23px] font-bold leading-[1.2] tracking-tight text-foreground">{item.title}</h3>
              <p className="mx-auto mt-[12px] max-w-[292px] text-[14px] leading-[1.62] text-muted-foreground">{item.description}</p>
            </article>
          ))}
        </div>
      </section>

      <section id="solutions" className="section-shell">
        <div className="mx-auto max-w-[1280px]">
          <div className="text-center">
            <h2 className="why-title">Solutions</h2>
          </div>

          <div className="mt-[38px] grid items-center gap-10 lg:grid-cols-[1.03fr_0.97fr] lg:gap-[52px]">
            <div className="solution-visual-wrap relative mx-auto flex min-h-[420px] w-full max-w-[500px] items-center justify-center overflow-hidden rounded-[34px]">
              <div className="solution-visual-bg absolute left-[6%] top-[16%] h-[290px] w-[420px] rounded-[26px] bg-secondary/70 shadow-card" />

              <div className="absolute left-[2%] top-[10%] z-30 flex h-[44px] w-[44px] items-center justify-center rounded-[14px] bg-violet shadow-lift">
                <Sparkles className="h-5 w-5 text-card" strokeWidth={2.4} />
              </div>
              <div className="absolute right-[4%] top-[6%] z-30 flex h-[44px] w-[44px] items-center justify-center rounded-full bg-amber shadow-lift">
                <Zap className="h-5 w-5 text-card" strokeWidth={2.6} fill="currentColor" />
              </div>
              <div className="absolute bottom-[8%] left-[8%] z-30 flex h-[44px] w-[44px] items-center justify-center rounded-[14px] bg-brand shadow-lift">
                <MessageCircle className="h-5 w-5 text-card" strokeWidth={2.4} />
              </div>

              <div className="solution-phone relative h-[365px] w-[240px] rounded-[34px] border-[4px] border-foreground bg-card shadow-lift">
                <div className="mx-auto mt-[12px] h-[18px] w-[76px] rounded-full bg-foreground" />
                <div className="mt-[36px] flex flex-col gap-[10px] px-[16px]">
                  <div className="h-[24px] w-[62%] self-start rounded-[12px] border border-dashed border-foreground/80" />
                  <div className="h-[24px] w-[70%] self-end rounded-[12px] border border-dashed border-foreground/80" />
                  <div className="h-[24px] w-[52%] self-start rounded-[12px] border border-dashed border-foreground/80" />
                  <div className="h-[24px] w-[66%] self-end rounded-[12px] border border-dashed border-foreground/80" />
                  <div className="h-[24px] w-[46%] self-start rounded-[12px] border border-dashed border-foreground/80" />
                </div>
              </div>

              <div className="solution-bubble absolute left-[4%] top-[56%] flex w-[194px] items-center gap-3 rounded-[18px] bg-card px-[14px] py-[12px] shadow-soft">
                <div className="flex h-[44px] w-[44px] items-center justify-center rounded-[14px] bg-lime">
                  <Star className="h-5 w-5 fill-card text-card" strokeWidth={2.3} />
                </div>
                <div className="flex-1">
                  <div className="h-[10px] w-[104px] rounded-full bg-muted" />
                  <div className="mt-[8px] h-[10px] w-[84px] rounded-full bg-muted/90" />
                </div>
              </div>

              <div className="solution-bubble absolute left-[50%] top-[30%] flex w-[194px] -translate-x-[4%] items-center gap-3 rounded-[18px] bg-card px-[14px] py-[12px] shadow-soft">
                <div className="flex h-[44px] w-[44px] items-center justify-center rounded-full bg-sky">
                  <Mic className="h-5 w-5 text-card" strokeWidth={2.4} />
                </div>
                <div className="flex flex-1 items-center gap-[6px]">
                  <div className="h-[14px] w-[9px] rounded-full bg-muted/60" />
                  <div className="h-[20px] w-[5px] rounded-full bg-muted/70" />
                  <div className="h-[28px] w-[6px] rounded-full bg-muted/80" />
                  <div className="h-[16px] w-[5px] rounded-full bg-muted/65" />
                  <div className="h-[22px] w-[6px] rounded-full bg-muted/85" />
                  <div className="h-[12px] w-[5px] rounded-full bg-muted/60" />
                  <div className="h-[18px] w-[5px] rounded-full bg-muted/75" />
                </div>
              </div>

              <div className="solution-bubble absolute bottom-[6px] left-[56%] w-[102px] rounded-[18px] bg-card px-[16px] py-[12px] text-center shadow-soft">
                <div className="mx-auto flex h-[40px] w-[40px] items-center justify-center rounded-full border-[2.5px] border-foreground/90 text-foreground">
                  <UserRound className="h-[22px] w-[22px]" strokeWidth={2.3} />
                </div>
                <div className="mx-auto mt-[14px] h-[10px] w-[52px] rounded-full bg-muted" />
                <div className="mx-auto mt-[8px] h-[10px] w-[42px] rounded-full bg-muted/90" />
              </div>
            </div>

            <div className="space-y-[28px]">
              {solutionCards.map((item) => (
                <article key={item.title} className="solution-card flex items-center gap-6 rounded-[16px] border border-border/80 bg-card/80 px-[22px] py-[20px] shadow-card">
                  <div className="min-w-0 flex-1">
                    <h3 className="text-[17px] font-bold leading-[1.3] text-foreground">{item.title}</h3>
                    <p className="mt-[8px] max-w-[470px] text-[14px] leading-[1.5] text-foreground/80">{item.description}</p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section id="use-cases" className="section-shell relative z-20">
        <div className="text-center">
          <h2 className="why-title">Use Cases</h2>
        </div>
        <div className="mx-auto mt-[38px] grid max-w-[1280px] gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {useCases.map((item) => {
            const isOpen = openCase === item.title;
            return (
            <article
              key={item.title}
              className={`usecase-card group relative overflow-visible rounded-[18px] border border-border/80 bg-card shadow-card transition-all duration-300 hover:-translate-y-[6px] hover:shadow-lift ${isOpen ? "z-40" : "z-10"}`}
            >
              <div className={`${item.headerClass} usecase-card-top relative h-[124px] overflow-hidden rounded-t-[18px] p-5`}>
                {item.decor?.map((decor, index) => (
                  <decor.Icon
                    key={`${item.title}-${index}`}
                    aria-hidden="true"
                    className={`absolute text-white/95 drop-shadow-[0_1px_2px_rgba(0,0,0,0.18)] ${decor.className}`}
                    strokeWidth={2}
                  />
                ))}
              </div>
              <div className="flex min-h-[124px] flex-col p-[15px]">
                <h3 className="text-[16px] font-bold leading-[1.25] tracking-tight text-foreground">{item.title}</h3>
                <p className="mt-[8px] max-w-[190px] text-[13px] leading-[1.45] text-muted-foreground">{item.description}</p>
                {item.detail ? (
                  <button
                    type="button"
                    onClick={() => setOpenCase(isOpen ? null : item.title)}
                    aria-expanded={isOpen}
                    className="mt-auto self-end pt-3 text-[14px] font-bold leading-none text-foreground/85 hover:text-primary"
                  >
                    {isOpen ? "Less" : "More"}
                  </button>
                ) : (
                  <div className="mt-auto flex justify-end pt-3 text-[14px] font-bold leading-none text-foreground/85">More</div>
                )}
              </div>
              {item.detail ? (
                <div
                  className={`usecase-detail absolute left-1/2 top-[calc(100%+8px)] z-50 w-[280px] max-w-[calc(100vw-32px)] -translate-x-1/2 rounded-[14px] border border-border/80 bg-card px-[14px] py-[12px] text-[12px] leading-[1.6] text-foreground shadow-lift transition-opacity duration-200 ${isOpen ? "opacity-100 pointer-events-auto" : "pointer-events-none opacity-0 md:group-hover:opacity-100 md:group-hover:pointer-events-auto"}`}
                >
                  {item.detail}
                </div>
              ) : null}
            </article>
            );
          })}
        </div>
      </section>

      <section id="industries" className="section-shell">
        <div className="mx-auto max-w-[900px] text-center">
          <h2 className="why-title">Industries</h2>
          <p className="mx-auto mt-[8px] max-w-[520px] text-[14px] leading-[1.45] text-muted-foreground">
            Messaging and voice solutions tailored for high-demand industries where delivery, speed, and reliability matter.
          </p>
        </div>
        <div className="mx-auto mt-[34px] grid max-w-[1100px] grid-cols-1 gap-5 sm:grid-cols-2 md:grid-cols-6">
          {industries.map((industry, i) => (
            <article
              key={industry.title}
              className={`flex flex-col items-center rounded-[18px] bg-card px-6 py-7 text-center shadow-card transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lift md:col-span-2 ${i === 6 ? "md:col-start-2" : ""} ${i === 7 ? "md:col-start-4" : ""}`}
            >
              <industry.Icon className={`h-[52px] w-[52px] ${industry.color}`} strokeWidth={1.75} aria-hidden="true" />
              <h3 className="mt-4 text-[16px] font-bold text-foreground">{industry.title}</h3>
              <div className="mt-2 space-y-0.5 text-[13px] leading-[1.55] text-muted-foreground">
                {industry.lines.map((l) => <p key={l}>{l}</p>)}
              </div>
            </article>
          ))}
        </div>
      </section>

      <ReachingYooTicker />

      <section id="how-it-works" className="section-shell">
        <div className="mx-auto max-w-[1280px] rounded-[28px] bg-[#E8F6EF] px-4 py-12 md:px-10 md:py-16">
          <div className="text-center">
            <h2 className="why-title text-[28px] md:text-[38px]">How It Works</h2>
          </div>
          <div className="relative mx-auto mt-[28px] min-h-[470px] max-w-[1160px] overflow-hidden md:min-h-[520px]">
            <div className="absolute left-1/2 top-[68px] z-10 h-[360px] w-[214px] -translate-x-1/2 rounded-[40px] border-[4px] border-foreground bg-card shadow-lift md:h-[390px] md:w-[224px]">
              <div className="mx-auto mt-[10px] h-[18px] w-[74px] rounded-full bg-foreground" />
              <div className="mt-[24px] flex flex-col gap-[10px] px-[14px]">
                <div className="h-[26px] w-[62%] self-start rounded-[12px] border border-dashed border-foreground/70" />
                <div className="h-[26px] w-[70%] self-end rounded-[12px] border border-dashed border-foreground/70" />
                <div className="h-[26px] w-[50%] self-start rounded-[12px] border border-dashed border-foreground/70" />
                <div className="h-[26px] w-[66%] self-end rounded-[12px] border border-dashed border-foreground/70" />
                <div className="h-[26px] w-[44%] self-start rounded-[12px] border border-dashed border-foreground/70" />
              </div>
            </div>

            <AudioWaveform aria-hidden="true" className="how-float absolute left-[64%] top-[122px] z-20 h-[74px] w-[74px] text-brand md:h-[88px] md:w-[88px]" strokeWidth={1.75} />
            <MessageSquare aria-hidden="true" className="how-float-delayed absolute left-[81%] top-[138px] z-20 h-[58px] w-[58px] text-violet md:h-[72px] md:w-[72px]" strokeWidth={1.75} />

            <article className="how-card how-float-delayed absolute left-[10%] top-[154px] z-30 w-[250px] rounded-[14px] bg-violet-sweep px-[18px] py-[14px] text-card opacity-100 md:w-[340px] md:px-[22px] md:py-[16px]">
              <div className="flex items-start gap-3">
                <Plug aria-hidden="true" className="mt-[2px] h-[22px] w-[22px] text-card md:h-[24px] md:w-[24px]" strokeWidth={2} />
                <div>
                  <h3 className="text-[16px] font-bold leading-none text-card md:text-[17px]">Connect</h3>
                  <p className="mt-[7px] text-[11px] leading-[1.45] text-card md:text-[12px]">Easily integrate using REST API or SMPP in minutes.</p>
                </div>
              </div>
            </article>

            <article className="how-card how-float absolute left-[43%] top-[250px] z-30 w-[270px] rounded-[14px] bg-sky-sweep px-[18px] py-[14px] text-card opacity-100 md:w-[360px] md:px-[22px] md:py-[16px]">
              <div className="flex items-start gap-3">
                <Zap aria-hidden="true" className="mt-[2px] h-[22px] w-[22px] text-card md:h-[24px] md:w-[24px]" strokeWidth={2} />
                <div>
                  <h3 className="text-[16px] font-bold leading-none text-card md:text-[17px]">Route</h3>
                  <p className="mt-[7px] text-[11px] leading-[1.45] text-card md:text-[12px]">Send traffic through optimized global routes for speed and reliability.</p>
                </div>
              </div>
            </article>

            <article className="how-card how-float absolute left-[10%] top-[350px] z-30 w-[250px] rounded-[14px] bg-brand-sweep px-[18px] py-[14px] text-card opacity-100 md:w-[340px] md:px-[22px] md:py-[16px]">
              <div className="flex items-start gap-3">
                <TrendingUp aria-hidden="true" className="mt-[2px] h-[22px] w-[22px] text-card md:h-[24px] md:w-[24px]" strokeWidth={2} />
                <div>
                  <h3 className="text-[16px] font-bold leading-none text-card md:text-[17px]">Monitor</h3>
                  <p className="mt-[7px] text-[11px] leading-[1.45] text-card md:text-[12px]">Track delivery and performance in real time.</p>
                </div>
              </div>
            </article>
          </div>
        </div>
      </section>

      <section id="about" className="section-shell">
        <div className="mx-auto max-w-[900px] text-center">
          <h2 className="section-title">Frequently Asked Questions</h2>
        </div>
        <div className="mx-auto mt-10 max-w-[1120px]">
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((item, index) => (
              <AccordionItem key={item.question} value={`item-${index}`} className="border-b border-border">
                <AccordionTrigger className="py-6 text-left text-lg font-bold hover:no-underline md:text-xl">
                  {item.question}
                </AccordionTrigger>
                <AccordionContent className="pb-6 pr-12 text-base leading-7 text-muted-foreground">
                  {item.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      <section id="blog" className="section-shell pt-0 md:pt-0">
        <div className="mx-auto max-w-[1280px]">
          <div className="text-center">
            <h2 className="why-title">Partners</h2>
          </div>
          <div className="mt-[10px] text-center text-[11px] font-bold uppercase tracking-[0.12em] text-[#8a9bb0]">
            Trusted by Global Operators &amp; Carriers
          </div>
          <div
            className="logos-marquee mt-[20px] border-y border-[#e8e6e1] bg-white"
            style={{
              overflow: "hidden",
              width: "100%",
              position: "relative",
              WebkitMaskImage:
                "linear-gradient(to right, transparent 0, black 120px, black calc(100% - 120px), transparent 100%)",
              maskImage:
                "linear-gradient(to right, transparent 0, black 120px, black calc(100% - 120px), transparent 100%)",
            }}
          >
            <div
              className="logos-marquee-track"
              style={{
                display: "flex",
                width: "max-content",
                willChange: "transform",
                animation: "partners-scroll 15s linear infinite",
                alignItems: "center",
                padding: "20px 0",
              }}
            >
              {Array.from({ length: 2 }).flatMap((_, half) =>
                partners.map((p, i) => (
                  <div
                    key={`${p.name}-${half}-${i}`}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      height: "60px",
                      padding: "0 40px",
                      flexShrink: 0,
                      borderRight: "1px solid #e8e6e1",
                    }}
                  >
                    {p.src ? (
                      <img
                        className="partner-logo"
                        src={p.src}
                        alt={`${p.name} logo`}
                        loading="lazy"
                        style={{
                          height: "36px",
                          width: "auto",
                          maxWidth: "120px",
                          objectFit: "contain",
                          backgroundColor: "transparent",
                          display: "block",
                        }}
                      />
                    ) : (
                      <VectorWordmark name={p.name} className="h-[36px] w-[120px] text-foreground/70" />
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </section>

      <UpcomingEvents />

      <section id="explore-solutions" className="section-shell">
        <div className="mx-auto max-w-[900px] text-center">
          <h2 className="why-title">Explore Our Solutions</h2>
        </div>
        <div className="mx-auto mt-[38px] grid max-w-[1100px] grid-cols-1 items-start gap-8 md:grid-cols-3 md:gap-5">
          {exploreGroups.map((group) => (
            <div key={group.title} className="flex flex-col">
              <h3 className="text-[17px] font-bold leading-[1.3] text-foreground">{group.title}</h3>
              <div className="mt-[18px] flex flex-col gap-1.5">
                {group.links.map((link) => {
                  const { Icon, title, to } = link;
                  return (
                    <Link
                      key={title}
                      to={to}
                      className="group flex items-center gap-2 py-1 text-[12px] font-medium leading-[1.4] text-foreground transition-colors hover:text-primary"
                    >
                      <Icon className="h-3 w-3 flex-none text-muted-foreground transition-colors group-hover:text-primary" strokeWidth={2.2} aria-hidden="true" />
                      <span>{title}</span>
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </section>

      <section id="contact" className="section-shell">
        <div className="cta-shell relative isolate overflow-hidden rounded-[36px] border border-border/70 px-6 py-14 text-center shadow-soft md:px-10 md:py-20">
          <div className="cta-grid-overlay absolute inset-0" aria-hidden="true" />
          <CtaTopScribble className="absolute right-5 top-4 z-0 h-auto w-[108px] opacity-80 md:right-8 md:top-6 md:w-[126px]" />

          <div className="relative z-10">
            <h2 className="section-title">
              <span className="text-sky">Ready</span> to get started?
            </h2>
            <div className="mx-auto mt-5 flex max-w-2xl flex-col items-center">
              <p className="text-lg text-muted-foreground">Talk to our team about your messaging and voice requirements. We respond within 24 hours.</p>
              <CtaUnderlineScribble className="mt-2 h-auto w-[92px]" />
            </div>
            <Link to="/contact" className="mt-8 inline-flex rounded-xl bg-primary px-8 py-4 text-base font-bold text-primary-foreground shadow-soft transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lift">
              Contact Sales
            </Link>
          </div>
        </div>
      </section>

      <SiteFooter />
    </main>
  );
};

export default Index;
